import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Controller, useForm } from "react-hook-form";
import {
  Edit,
  Eye,
  X,
  Search,
  Plus,
  Calendar,
  MapPin,
  Hash,
  Briefcase,
  ArrowUpDown,
  Info,
  MousePointer2,
  FileSearch,
  Send,
  AlertCircle,
  Loader2,
  Building2,
  Clock,
  Target,
  FileText,
  Phone,
  Mail,
  Wrench,
  UserCheck,
  DollarSign,
} from "lucide-react";
import { toast } from 'react-toastify';
import Select from "../../../shared/components/form/Select";
import { safeFormatDate, safeFormatDistance, safeCreateDate } from "../../../utils/dateUtils";
// Project details now open on dedicated page /projects/view/:projectId
import projectTypeService, { ProjectTypeDropdownOption } from "../../../services/projectTypeService";
import { userService } from "../../../services/userService";
import { projectService } from "../../../services/projectService";
import locationService from "../../../services/locationService";
import { useAuth } from "../../../context/AuthContext";
import RightDrawer from "../../../shared/components/form/RightDrawer";
import ProjectProgressHeader from "./common/ProjectProgressHeader";
import { ProjectApprovalDetails, getProjectApprovalDetails, approveProjectLevel, rejectProjectLevel } from "../../../services/projectApprovalService";
// ViewToggle removed - grid view only
import ProjectCard from "./common/ProjectCard";
import ProjectFilterBar from "./common/ProjectFilterBar";
import {
  ProjectStatusInfo,
  getProjectStatusInfo,
  needsApproval,
  getNormalizedApprovalStatus,
  STATUS_STYLE_MAP,
  ProjectStatusTone
} from "../utils/projectStatusUtils";
import { validateProjectForReviewSend } from "../utils/validateProjectForReview";

// Modern Clean Brand Colors - Subtle & Refined
const BRAND_COLORS = {
  primary: "#339274",          // Primary Green
  primaryHover: "#29795f",     // Darker Green for hover
  primaryLight: "#F8FCFA",     // Very subtle green background
  primarySoft: "#EDF7F2",      // Soft green background
  primaryBorder: "#D1E7DD",    // Light green border
  accent: "#A3CD39",           // Lime accent
  accentLight: "#F7FAF0",      // Light lime background
  dark: "#007F66",             // Deep teal
  neutral: {
    25: "#FCFCFD",
    50: "#F9FAFB",
    100: "#F3F4F6",
    200: "#E5E7EB",
    300: "#D1D5DB",
    400: "#9CA3AF",
    500: "#6B7280",
    600: "#4B5563",
    700: "#374151",
    800: "#1F2937",
    900: "#111827"
  },
  success: "#10B981",
  successLight: "#F0FDF4",
  error: "#EF4444",
  errorLight: "#FEF2F2",
  warning: "#F59E0B",
  warningLight: "#FFFBEB",
};




interface Props {
  projects: any[];
  search: string;
  setSearch: (val: string) => void;
  projectTypeId: string;
  setProjectTypeId: (val: string) => void;
  location: string;
  setLocation: (val: string) => void;
  approvalStatus: string;
  setApprovalStatus: (val: string) => void;
  page: number;
  setPage: (val: number) => void;
  totalProjects: number;
  onEdit?: (project: any) => void;
  /** Primary: open the classic project form (e.g. `/projects/create`). */
  onAdd?: () => void;
  /** Optional: open AI-assisted project flow (e.g. `/projects/ai-create`); shown as a small text link. */
  onAddWithAI?: () => void;
  /** Label for the primary add button. */
  addButtonLabel?: string;
  /** Label for the secondary AI create link. */
  aiCreateLinkLabel?: string;
  onSelect?: (project: any) => void;
  onOpenAssetDrawer?: (project: any) => void;
  onOpenCalendar?: (project: any) => void;
  selectedProject?: any;
  onRefresh?: () => void;
  sortField: string;
  sortOrder: "asc" | "desc";
  setSortField: (field: string) => void;
  setSortOrder: (order: "asc" | "desc") => void;
  loading?: boolean;
}

const ITEMS_PER_PAGE = 12;

export default function ProjectTable({
  projects,
  search,
  setSearch,
  projectTypeId,
  setProjectTypeId,
  location,
  setLocation,
  approvalStatus,
  setApprovalStatus,
  page,
  setPage,
  totalProjects,
  onEdit,
  onAdd,
  onAddWithAI,
  addButtonLabel = "Create project",
  aiCreateLinkLabel = "Create with AI Architect",
  onSelect,
  onOpenAssetDrawer,
  onOpenCalendar,
  onRefresh,
  sortField,
  sortOrder,
  setSortField,
  setSortOrder,
  loading = false,
}: Props) {
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [sortMenuOpen, setSortMenuOpen] = useState<string | null>(null);
  const [userCache, setUserCache] = useState<Record<string, string>>({});
  const [projectLevelPanelProject, setProjectLevelPanelProject] = useState<any>(null);
  const [showProjectLevelPanel, setShowProjectLevelPanel] = useState(false);
  const [projectLevelPanelLoading, setProjectLevelPanelLoading] = useState(false);
  const [projectLevelDetails, setProjectLevelDetails] = useState<ProjectApprovalDetails | null>(null);
  const [projectLevelComment, setProjectLevelComment] = useState('');
  const [projectLevelActionLoading, setProjectLevelActionLoading] = useState(false);

  // Get current user role
  const { user } = useAuth();

  // Check if user is a requester
  const isRequester = user?.role?.roleType === 'requester' || user?.role?.roleType === 'both';



  // Priority-based sorting function for projects
  const getProjectPriority = (project: any): number => {
    const status = project.approvalStatus;

    // Priority order: pending approval (1) → rejected (2) → started (3) → others (4)
    if (status === 'pending_approval') return 1;
    if (status === 'rejected') return 2;
    if (status === 'approved') {
      const startDateRaw = project?.actualStartDate || project?.startDate || project?.plannedStartDate;
      if (startDateRaw) {
        const startDate = new Date(startDateRaw);
        const today = new Date();
        if (startDate <= today) return 3; // Started projects
      }
      return 4; // Approved but not started
    }
    if (status === 'partially_approved') return 5;
    if (status === 'draft') return 6;
    return 7; // Others
  };

  // Sort projects - default to latest created first, otherwise use priority-based sorting
  const sortedProjects = useMemo(() => {
    if (!projects || projects.length === 0) return [];

    // If sorting by createdAt desc (default), just return projects as-is (already sorted by backend)
    // Otherwise, apply priority-based sorting
    if (sortField === "createdAt" && sortOrder === "desc") {
      return projects;
    }

    // Apply priority-based sorting for other sort options
    return [...projects].sort((a, b) => {
      const priorityA = getProjectPriority(a);
      const priorityB = getProjectPriority(b);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // Secondary sort by creation date (newest first)
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();
      return dateB - dateA;
    });
  }, [projects, sortField, sortOrder]);



  const handleViewProject = (project: any) => {
    const id = project._id || project.id;
    if (id) navigate(`/projects/view/${id}`);
  };

  const roleType = (user?.role?.roleType ?? '').toLowerCase();
  const roleName = (user?.role?.name ?? '').toLowerCase();
  const canMakeProjectLevelDecision =
    Boolean(user) &&
    ((roleType && roleType !== 'requester') || roleName.includes('head') || roleName.includes('admin'));

  const fetchProjectLevelDetails = async (projectId: string) => {
    setProjectLevelPanelLoading(true);
    try {
      const details = await getProjectApprovalDetails(projectId);
      setProjectLevelDetails(details);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to load project-level details');
      setProjectLevelDetails(null);
    } finally {
      setProjectLevelPanelLoading(false);
    }
  };

  const handleOpenProjectLevelPanel = (project: any) => {
    const projectId = project?._id || project?.id;
    if (!projectId) {
      toast.error('Project identifier missing');
      return;
    }
    setProjectLevelPanelProject(project);
    setShowProjectLevelPanel(true);
    setProjectLevelComment('');
    setProjectLevelDetails(null);
    void fetchProjectLevelDetails(projectId);
  };

  const handleCloseProjectLevelPanel = () => {
    setShowProjectLevelPanel(false);
    setProjectLevelPanelProject(null);
    setProjectLevelDetails(null);
    setProjectLevelComment('');
  };

  const handleRefreshProjectLevelDetails = () => {
    const projectId = projectLevelPanelProject?._id || projectLevelPanelProject?.id;
    if (projectId) {
      void fetchProjectLevelDetails(projectId);
    }
  };

  const handleProjectLevelApprove = async () => {
    const projectId = projectLevelPanelProject?._id || projectLevelPanelProject?.id;
    if (!projectId) return;
    try {
      setProjectLevelActionLoading(true);
      await approveProjectLevel(projectId);
      toast.success('Project-level approval recorded');
      setProjectLevelPanelProject((prev: any) =>
        prev ? { ...prev, projectLevelApprovalStatus: 'approved' as const } : prev
      );
      setProjectLevelComment('');
      onRefresh?.();
      await fetchProjectLevelDetails(projectId);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to approve project');
    } finally {
      setProjectLevelActionLoading(false);
    }
  };

  const handleProjectLevelReject = async () => {
    const projectId = projectLevelPanelProject?._id || projectLevelPanelProject?.id;
    if (!projectId) return;
    if (!projectLevelComment.trim()) {
      toast.error('Please add a comment before rejecting the project');
      return;
    }
    try {
      setProjectLevelActionLoading(true);
      await rejectProjectLevel(projectId, projectLevelComment.trim());
      toast.success('Project rejected at project level');
      setProjectLevelPanelProject((prev: any) =>
        prev ? { ...prev, projectLevelApprovalStatus: 'rejected' as const, projectLevelApproverComments: projectLevelComment.trim() } : prev
      );
      onRefresh?.();
      await fetchProjectLevelDetails(projectId);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to reject project');
    } finally {
      setProjectLevelActionLoading(false);
    }
  };

  const displayValidationErrors = (errors: string[]) => {
    toast.error(
      <div>
        <div className="font-bold mb-2">Cannot send for review:</div>
        <ul className="list-disc pl-4 space-y-1">
          {errors.map((error, index) => (
            <li key={index} className="text-sm">{error}</li>
          ))}
        </ul>
      </div>,
      { autoClose: 8000 }
    );
  };

  const validateProjectBeforeSubmission = async (projectId: string, projectSnapshot: any) => {
    return validateProjectForReviewSend({
      projectId,
      project: projectSnapshot as Record<string, unknown>
    });
  };

  // Direct submission without review drawer
  const handleSubmitForApproval = async (project: any) => {
    try {
      const projectId = project._id || project.id;
      if (!projectId) {
        toast.error('Project ID is missing. Please refresh and try again.');
        return;
      }

      const validation = await validateProjectBeforeSubmission(projectId, project);
      if (!validation.valid) {
        displayValidationErrors(validation.errors);
        return;
      }

      await projectService.submitProjectForApproval(projectId, {
        comments: 'Project ready for approval'
      });

      toast.success('✅ Project submitted for approval successfully!');
      onRefresh?.();
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit project');
      console.error(error);
    }
  };


  // Function to get user name from ID
  const getUserName = async (userId: string): Promise<string> => {
    if (userCache[userId]) {
      return userCache[userId];
    }

    try {
      const user = await userService.getUserById(userId);
      const userName = user.name || user.email || 'Unknown User';
      setUserCache(prev => ({ ...prev, [userId]: userName }));
      return userName;
    } catch (error) {
      console.error('Failed to fetch user:', error);
      return 'Unknown User';
    }
  };

  // Function to get display name for createdBy
  const getCreatedByDisplayName = (createdBy: any): string => {
    if (typeof createdBy === 'object' && createdBy?.name) {
      return createdBy.name;
    }
    if (typeof createdBy === 'string') {
      // If it's a string (likely an ID), we'll show a loading state initially
      // and fetch the user name asynchronously
      return userCache[createdBy] || 'Loading...';
    }
    return 'Unknown User';
  };

  // Handle sort options from dropdown
  const handleSortOption = (field: string, order: "asc" | "desc" | "clear") => {
    if (order === "clear") {
      setSortField("createdAt");
      setSortOrder("desc");
    } else {
      setSortField(field);
      setSortOrder(order);
    }
    setSortMenuOpen(null);
    setPage(1);
  };

  // Fetch user names for createdBy fields
  useEffect(() => {
    const fetchUserNames = async () => {
      const createdByIds = projects
        .map(project => project.createdBy)
        .filter(createdBy => typeof createdBy === 'string' && !userCache[createdBy]);

      // Combine and remove duplicates
      const userIds = [...new Set(createdByIds)];

      for (const userId of userIds) {
        await getUserName(userId);
      }
    };

    if (projects.length > 0) {
      fetchUserNames();
    }
  }, [projects, userCache]);

  // Search keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Close sort dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.sort-dropdown-container')) {
        setSortMenuOpen(null);
      }
    };

    if (sortMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [sortMenuOpen]);

  // Calculate pagination
  const totalPages = Math.ceil(totalProjects / ITEMS_PER_PAGE);
  const startItem = (page - 1) * ITEMS_PER_PAGE + 1;
  const endItem = Math.min(page * ITEMS_PER_PAGE, totalProjects);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
      range.push(i);
    }

    if (page - delta > 2) {
      rangeWithDots.push(1, "...");
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (page + delta < totalPages - 1) {
      rangeWithDots.push("...", totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };


  const getProjectId = (project: any) => project._id || project.id || project.projectId;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100"
      >
        {/* Command bar: search + filters + sort + add */}
        <div className="p-4 sm:p-5 border-b border-gray-100 bg-white font-[family-name:var(--font-outfit)]">
          <div className="flex flex-col gap-4">
            {/* Top row: search (full width on mobile) + sort + add */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative flex-1 min-w-0 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="block w-full pl-9 pr-20 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-900 placeholder-gray-400 bg-gray-50/50 focus:bg-white focus:ring-2 focus:border-transparent focus:outline-none transition-all duration-200"
                  style={
                    {
                      "--tw-ring-color": BRAND_COLORS.primary,
                    } as React.CSSProperties
                  }
                  placeholder="Search by project name…"
                />
                <div className="absolute inset-y-0 right-0 pr-2 flex items-center gap-1">
                  <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold text-gray-400 bg-white border border-gray-200 rounded">
                    ⌘K
                  </kbd>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <div className="relative sort-dropdown-container">
                  <button
                    onClick={() =>
                      setSortMenuOpen(sortMenuOpen ? null : "general")
                    }
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 focus:outline-none focus:ring-2 transition-all duration-200"
                    style={
                      {
                        "--tw-ring-color": BRAND_COLORS.primary,
                      } as React.CSSProperties
                    }
                  >
                    <ArrowUpDown className="w-4 h-4" />
                    Sort
                  </button>

                  <AnimatePresence>
                    {sortMenuOpen === "general" && (
                      <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="absolute top-full right-0 mt-1.5 z-20 w-44 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden py-1.5"
                      >
                        <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-1.5">
                          Name
                        </div>
                        <button
                          onClick={() =>
                            handleSortOption("projectName", "asc")
                          }
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg mx-1 transition ${
                            sortField === "projectName" && sortOrder === "asc"
                              ? "font-semibold"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                          style={
                            sortField === "projectName" && sortOrder === "asc"
                              ? {
                                  color: BRAND_COLORS.primary,
                                  backgroundColor: BRAND_COLORS.primaryLight,
                                }
                              : {}
                          }
                        >
                          A → Z
                        </button>
                        <button
                          onClick={() =>
                            handleSortOption("projectName", "desc")
                          }
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg mx-1 transition ${
                            sortField === "projectName" && sortOrder === "desc"
                              ? "font-semibold"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                          style={
                            sortField === "projectName" && sortOrder === "desc"
                              ? {
                                  color: BRAND_COLORS.primary,
                                  backgroundColor: BRAND_COLORS.primaryLight,
                                }
                              : {}
                          }
                        >
                          Z → A
                        </button>
                        <div className="border-t border-gray-100 my-1.5" />
                        <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider px-3 py-1.5">
                          Date
                        </div>
                        <button
                          onClick={() => handleSortOption("createdAt", "desc")}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg mx-1 transition ${
                            sortField === "createdAt" && sortOrder === "desc"
                              ? "font-semibold"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                          style={
                            sortField === "createdAt" && sortOrder === "desc"
                              ? {
                                  color: BRAND_COLORS.primary,
                                  backgroundColor: BRAND_COLORS.primaryLight,
                                }
                              : {}
                          }
                        >
                          Newest first
                        </button>
                        <button
                          onClick={() => handleSortOption("createdAt", "asc")}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg mx-1 transition ${
                            sortField === "createdAt" && sortOrder === "asc"
                              ? "font-semibold"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                          style={
                            sortField === "createdAt" && sortOrder === "asc"
                              ? {
                                  color: BRAND_COLORS.primary,
                                  backgroundColor: BRAND_COLORS.primaryLight,
                                }
                              : {}
                          }
                        >
                          Oldest first
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {(onAdd || onAddWithAI) && (
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    {onAdd && (
                      <button
                        type="button"
                        onClick={onAdd}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200"
                        style={{
                          background: `linear-gradient(135deg, ${BRAND_COLORS.primary}, ${BRAND_COLORS.primaryHover})`,
                          "--tw-ring-color": BRAND_COLORS.primary,
                        } as React.CSSProperties}
                      >
                        <Plus className="w-4 h-4" />
                        {addButtonLabel}
                      </button>
                    )}
                    {onAddWithAI && (
                      <button
                        type="button"
                        onClick={onAddWithAI}
                        className="text-xs sm:text-sm font-medium text-gray-500 underline-offset-2 hover:underline hover:text-gray-800 whitespace-nowrap"
                      >
                        {aiCreateLinkLabel}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Filters: status pills + location/type */}
            <ProjectFilterBar
              projectTypeId={projectTypeId}
              setProjectTypeId={setProjectTypeId}
              location={location}
              setLocation={setLocation}
              approvalStatus={approvalStatus}
              setApprovalStatus={setApprovalStatus}
              setPage={setPage}
            />
          </div>
        </div>

        {/* Loading State */}
        {
          loading && (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: BRAND_COLORS.primary }}></div>
            </div>
          )
        }

        {/* Grid View - Always Shown */}
        {
          !loading && (
            <div className="p-6">
              {projects.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-gray-500 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                  <Briefcase className="w-12 h-12 text-gray-300 mb-3" />
                  <p className="text-lg font-medium text-gray-900">No projects found</p>
                  <p className="text-sm mt-1">Try adjusting your filters or add a new project.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  <AnimatePresence>
                    {sortedProjects.map((project) => (
                      <ProjectCard
                        key={getProjectId(project)}
                        project={project}
                        creatorName={
                          project.createdBy && typeof project.createdBy === 'string'
                            ? userCache[project.createdBy]
                            : project.createdBy?.name
                        }
                        onView={handleViewProject}
                        onEdit={onEdit}
                        onOpenAssetDrawer={onOpenAssetDrawer || onSelect}
                        onOpenCalendar={onOpenCalendar}
                        onSubmitForApproval={handleSubmitForApproval}
                        isRequester={isRequester}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>
          )
        }

        {/* Pagination */}
        {
          totalProjects > 0 && (
            <div className="px-6 py-4 border-t border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                {/* Results Info */}
                <div className="text-sm text-gray-600">
                  Showing <span className="font-medium">{startItem}</span> to{" "}
                  <span className="font-medium">{endItem}</span> of{" "}
                  <span className="font-medium">{totalProjects}</span> projects
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      Previous
                    </button>

                    <div className="hidden sm:flex items-center gap-1">
                      {getPageNumbers().map((pageNum, idx) =>
                        pageNum === "..." ? (
                          <span key={`ellipsis-${idx}`} className="px-3 py-2 text-gray-400">
                            ...
                          </span>
                        ) : (
                          <button
                            key={pageNum}
                            onClick={() => setPage(pageNum as number)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${page === pageNum
                              ? 'text-white border-transparent'
                              : 'text-gray-500 bg-white border border-gray-200 hover:bg-gray-50 hover:text-gray-700'
                              }`}
                            style={page === pageNum ? { backgroundColor: BRAND_COLORS.primary } : {}}
                          >
                            {pageNum}
                          </button>
                        )
                      )}
                    </div>

                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          )
        }

        {/* Project details: opened on dedicated page /projects/view/:projectId */}
      </motion.div>
    </>
  );
}